import { useSyncExternalStore } from 'react';

/**
 * The runtime colour switcher of the HyperTek prototype (h-prototype `src/config/theme.ts` +
 * `src/lib/theme-context.tsx`), reduced to a module store so nothing has to be wrapped in a provider: a
 * screen rendered on its own in a test simply sees the default palette.
 *
 * A palette overrides exactly the four properties the prototype's `applyTheme` sets — `--h-primary-h`,
 * `--h-primary`, `--h-primary-hover`, `--h-accent` — on `<html>` through `style.setProperty`: CSSOM, which
 * production's `style-src 'self'` allows (an injected `<style>` element would be refused). Surfaces, borders,
 * muted tones and the sidebar/rail tokens in tokens.css derive from `--h-primary-h` / `--h-primary`, so one
 * palette re-tints the whole app.
 *
 * The list is the prototype's — ids, names, order, HyperTek first — with one product rule on top: white text
 * on `primary` must reach WCAG AA (4.5:1), because every primary button carries it. Measured with the WCAG
 * 2.1 relative-luminance formula; the three palettes that fail are darkened by lightness only, to the first
 * whole percent that passes, and their hover moves by the same step so it stays that much darker:
 *
 *   palette   prototype primary      white on it   here                 white on it   hover
 *   teal      173 80% 40% (#14b8a5)  2.49          173 80% 29% (#0f8577)  4.52        35% → 24%  (6.13)
 *   green     142 72% 35% (#199a48)  3.65          142 72% 31% (#168840)  4.54        30% → 26%  (6.02)
 *   orange     25 95% 53% (#f97415)  2.79           25 95% 39% (#c25405)  4.60        48% → 34%  (5.77)
 *
 * The other six pass unchanged: hypertek 11.22, blue 6.62, violet 5.78, rose 4.92, indigo 4.63, fuchsia 4.57.
 */

export type ThemePalette = {
  readonly id: string;
  readonly name: string;
  /** Hue (0–360) that tints background, border and muted surfaces. */
  readonly hue: number;
  /** HSL channels without the `hsl()` wrapper, e.g. "215 89% 26%". */
  readonly primary: string;
  readonly primaryHover: string;
  readonly accent: string;
};

export const THEME_PALETTES: readonly [ThemePalette, ...ThemePalette[]] = [
  // FIRST-PAINT ANCHOR: `primary` + `hue` equal tokens.css `--h-primary` / `--h-primary-h`, so the default
  // never flashes. Accent is not anchored (the prototype does the same; it is used sparsely).
  { id: 'hypertek', name: 'HyperTek', hue: 215, primary: '215 89% 26%', primaryHover: '215 89% 21%', accent: '188 86% 53%' },
  { id: 'rose', name: 'Hồng', hue: 345, primary: '345 80% 48%', primaryHover: '345 80% 42%', accent: '188 86% 53%' },
  { id: 'fuchsia', name: 'Tím sen', hue: 292, primary: '292 72% 50%', primaryHover: '292 72% 44%', accent: '188 86% 53%' },
  { id: 'violet', name: 'Tím', hue: 250, primary: '250 80% 60%', primaryHover: '250 80% 52%', accent: '188 86% 53%' },
  { id: 'indigo', name: 'Chàm', hue: 234, primary: '234 56% 60%', primaryHover: '234 50% 54%', accent: '188 86% 53%' },
  { id: 'blue', name: 'Xanh dương', hue: 220, primary: '220 95% 45%', primaryHover: '220 95% 40%', accent: '188 86% 53%' },
  { id: 'teal', name: 'Xanh ngọc', hue: 173, primary: '173 80% 29%', primaryHover: '173 80% 24%', accent: '188 86% 53%' },
  { id: 'green', name: 'Xanh lá', hue: 142, primary: '142 72% 31%', primaryHover: '142 72% 26%', accent: '188 86% 53%' },
  { id: 'orange', name: 'Cam', hue: 25, primary: '25 95% 39%', primaryHover: '25 95% 34%', accent: '188 86% 53%' },
];

const DEFAULT_PALETTE = THEME_PALETTES[0];
const STORAGE_KEY = 'h-operation-theme';

/**
 * Ids this product stored before it adopted the prototype's list. `darkblue` was the same HyperTek blue under
 * another name; `darkred` has no counterpart and falls through to the default like any unknown id.
 */
const RENAMED: Readonly<Record<string, string>> = { darkblue: 'hypertek' };

let current: ThemePalette = DEFAULT_PALETTE;
const listeners = new Set<() => void>();

function apply(theme: ThemePalette): void {
  const root = document.documentElement;
  root.style.setProperty('--h-primary-h', String(theme.hue));
  root.style.setProperty('--h-primary', theme.primary);
  root.style.setProperty('--h-primary-hover', theme.primaryHover);
  root.style.setProperty('--h-accent', theme.accent);
}

/** The palette a stored id stands for: itself, its renamed equivalent, or none. Never throws. */
export function paletteFor(id: string | null): ThemePalette | undefined {
  if (id === null) return undefined;
  const wanted = Object.hasOwn(RENAMED, id) ? RENAMED[id] : id;
  return THEME_PALETTES.find((palette) => palette.id === wanted);
}

/**
 * Called once from main.tsx before the first render, so a saved palette is on screen from the first paint.
 * A stored id that no longer exists leaves the default in place — tokens.css already paints it.
 */
export function restoreTheme(): void {
  let saved: string | null = null;
  try {
    saved = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage can be blocked (private mode, a locked-down profile); the default palette is the answer then.
  }
  const found = paletteFor(saved);
  if (found === undefined) return;
  current = found;
  apply(found);
}

export function setTheme(theme: ThemePalette): void {
  current = theme;
  apply(theme);
  try {
    window.localStorage.setItem(STORAGE_KEY, theme.id);
  } catch {
    // Not persisted, still applied: the choice lasts for this tab, which is the honest fallback.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * WCAG 2.1 relative luminance of an `h s% l%` channels string (a token's value), rounded to 8-bit channels as a
 * browser paints it.
 */
export function relativeLuminance(channels: string): number {
  const [h = 0, sPct = 0, lPct = 0] = channels.replace(/%/g, '').trim().split(/\s+/).map(Number);
  const s = sPct / 100;
  const l = lPct / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const linear = (c: number) => {
    const v = Math.round(c * 255) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(f(0)) + 0.7152 * linear(f(8)) + 0.0722 * linear(f(4));
}

/** WCAG 2.1 contrast ratio of two `h s% l%` colours (1 to 21). */
export function contrastRatio(a: string, b: string): number {
  const [light, dark] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x) as [number, number];
  return (light + 0.05) / (dark + 0.05);
}

export function useTheme(): { theme: ThemePalette; setTheme: (theme: ThemePalette) => void } {
  const theme = useSyncExternalStore(subscribe, () => current, () => DEFAULT_PALETTE);
  return { theme, setTheme };
}
